/**
 * IndexedDB Offline Queue Manager
 * 
 * Stores offline actions (create event, add bike, etc.) in IndexedDB
 * and syncs them when connection is restored
 * 
 * Features:
 * - Type-safe queue operations
 * - Automatic conflict detection
 * - Idempotency keys
 * - Priority ordering
 * - Error tracking
 */

export interface QueuedAction {
  id: string;                    // Unique ID (UUID)
  type: ActionType;              // Action type
  data: any;                     // Payload
  timestamp: number;             // Creation time
  retries: number;               // Retry count
  priority: number;              // Priority (1=highest)
  status: 'pending' | 'syncing' | 'failed';
  error?: string;                // Last error message
  idempotencyKey: string;        // Prevent duplicates
}

export type ActionType = 
  | 'create-event'
  | 'update-event'
  | 'join-event'
  | 'add-bike'
  | 'update-bike'
  | 'delete-bike'
  | 'update-profile'
  | 'upload-photo'
  // Richiesta HTTP generica accodata automaticamente da apiFetch quando la
  // rete cade su una scrittura (vedi utils/offlineRequestQueue.ts). Il replay
  // la ripete con apiFetch (URL assoluto + token), quindi funziona per OGNI
  // endpoint di scrittura, non solo per i tipi sopra (mappa storica parziale).
  | 'api-request';

const DB_NAME = 'rideconnect-offline';
const DB_VERSION = 2;
const STORE_NAME = 'action-queue';

/** Limite di sicurezza per le richieste accodate automaticamente. */
export const MAX_QUEUE_SIZE = 200;

let dbInstance: IDBDatabase | null = null;
let _indexedDBAvailable: boolean | null = null;

/**
 * Initialize IndexedDB
 * Returns null if IndexedDB is not available (legacy WebView, privacy mode).
 */
export async function initDB(): Promise<IDBDatabase | null> {
  if (dbInstance) return dbInstance;

  // Check IndexedDB availability once
  if (_indexedDBAvailable === null) {
    _indexedDBAvailable = typeof indexedDB !== 'undefined';
    if (!_indexedDBAvailable) {
      console.warn('[OfflineQueue] IndexedDB not available — offline mode disabled');
      return null;
    }
  }
  if (!_indexedDBAvailable) return null;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('priority', 'priority', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
  });
}

/**
 * Add action to queue
 */
export async function enqueueAction(
  type: ActionType,
  data: any,
  priority: number = 5
): Promise<QueuedAction> {
  const db = await initDB();

  const action: QueuedAction = {
    id: generateUUID(),
    type,
    data,
    timestamp: Date.now(),
    retries: 0,
    priority,
    status: 'pending',
    idempotencyKey: generateIdempotencyKey(type, data),
  };

  // Se IndexedDB non è disponibile, ritorna l'action senza persistere
  if (!db) {
    console.warn('[OfflineQueue] IndexedDB unavailable, action not persisted:', type);
    return action;
  }

  // Cap di sicurezza: le richieste accodate automaticamente da apiFetch
  // possono crescere senza limite (una per ogni scrittura fallita offline).
  // A coda piena si scarta la più vecchia automatica, così la coda resta
  // utilizzabile e il flush non diventa eterno.
  if (type === 'api-request') {
    await pruneOldestApiRequests(db);
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.add(action);

    request.onsuccess = () => resolve(action);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all pending actions
 */
export async function getPendingActions(): Promise<QueuedAction[]> {
  const db = await initDB();

  // FIX OFFLINE (Ott 2026): senza IndexedDB (WebView legacy, navigazione
  // privata) `db` è null e `db.transaction` lanciava un TypeError non gestito,
  // che faceva fallire anche il flush dell'hook (unhandled rejection).
  if (!db) return [];

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      // Sort by priority (1=highest) then timestamp
      const actions = request.result.sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.timestamp - b.timestamp;
      });
      resolve(actions);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update action status
 */
export async function updateAction(
  id: string,
  updates: Partial<QueuedAction>
): Promise<void> {
  const db = await initDB();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const action = getRequest.result;
      if (!action) {
        reject(new Error(`Action ${id} not found`));
        return;
      }

      const updated = { ...action, ...updates };
      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Remove action from queue
 */
export async function removeAction(id: string): Promise<void> {
  const db = await initDB();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all actions (for testing)
 */
export async function clearQueue(): Promise<void> {
  const db = await initDB();
  if (!db) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get queue statistics
 */
export async function getQueueStats(): Promise<{
  total: number;
  pending: number;
  syncing: number;
  failed: number;
}> {
  const db = await initDB();
  if (!db) return { total: 0, pending: 0, syncing: 0, failed: 0 };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const actions = request.result;
      resolve({
        total: actions.length,
        pending: actions.filter(a => a.status === 'pending').length,
        syncing: actions.filter(a => a.status === 'syncing').length,
        failed: actions.filter(a => a.status === 'failed').length,
      });
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Conta le richieste HTTP accodate automaticamente (tipo `api-request`).
 */
function getPendingApiRequestCount(
  actions: QueuedAction[],
): number {
  return actions.filter((a) => a.type === 'api-request').length;
}

/**
 * Mantiene la coda delle richieste automatiche entro MAX_QUEUE_SIZE,
 * rimuovendo la più vecchia quando il limite è superato.
 */
async function pruneOldestApiRequests(db: IDBDatabase): Promise<void> {
  const all = await new Promise<QueuedAction[]>((resolve) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve((req.result ?? []) as QueuedAction[]);
    req.onerror = () => resolve([]);
  });

  if (getPendingApiRequestCount(all) < MAX_QUEUE_SIZE) return;

  const oldest = all
    .filter((a) => a.type === 'api-request')
    .sort((a, b) => a.timestamp - b.timestamp)[0];
  if (!oldest) return;

  console.warn(
    `[OfflineQueue] Coda piena (${MAX_QUEUE_SIZE} richieste): scarto la più vecchia`,
    oldest.type,
  );
  await removeAction(oldest.id).catch(() => {});
}

/**
 * Generate UUID v4 — crittograficamente sicuro, nativo su tutte le WebView moderne.
 * Fallback per ambienti senza crypto (es. HTTP non sicuro in vecchi browser).
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback crittograficamente sicuro per ambienti senza crypto.randomUUID
  // (es. HTTP non sicuro in vecchi browser — raro nel 2026)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (crypto?.getRandomValues?.(new Uint8Array(1))?.[0] ?? 0) % 16;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate idempotency key to prevent duplicate actions.
 * Usa crypto.randomUUID + type prefix per garantire unicità e tracciabilità.
 */
function generateIdempotencyKey(type: ActionType, data: any): string {
  return `${type}-${generateUUID()}`;
}

export default {
  initDB,
  enqueueAction,
  getPendingActions,
  updateAction,
  removeAction,
  clearQueue,
  getQueueStats,
};
